import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import './SavedMessagesModal.css';

const PAGE_SIZE = 20;

const formatAmount = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) return null;
  return `${numericValue}€`;
};

const SavedMessagesModal = ({
  isOpen,
  onClose,
  subscriptionId,
  onApplySavedMessage,
}) => {
  const [collections, setCollections] = useState([]);
  const [selectedCollectionId, setSelectedCollectionId] = useState('');
  const [savedMessages, setSavedMessages] = useState([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState('');

  const fetchCollections = useCallback(async () => {
    const jwt = Cookies.get('user_token');
    if (!jwt || !subscriptionId) return [];

    const response = await axios.post(
      'https://unbot.io/api/v1.0/proxy',
      {
        method: 'GET',
        path: 'v3.0/messages/saved/collections',
        parameters: {},
        subscriptionId,
      },
      { headers: { Authorization: `Bearer ${jwt}` } }
    );

    if (response.data?.error) {
      throw new Error(response.data.error.message || 'Erreur de chargement');
    }

    return Array.isArray(response.data) ? response.data : [];
  }, [subscriptionId]);

  const fetchSavedMessages = useCallback(
    async (nextOffset = 0, append = false) => {
      const jwt = Cookies.get('user_token');
      if (!jwt || !subscriptionId) {
        setError("Impossible d'authentifier la requête.");
        return;
      }

      if (append) {
        setIsLoadingMore(true);
      } else {
        setIsLoading(true);
        setError('');
      }

      try {
        const parameters = {
          count: PAGE_SIZE,
          offset: nextOffset,
        };

        if (selectedCollectionId) {
          parameters.collectionId = selectedCollectionId;
        }

        const response = await axios.post(
          'https://unbot.io/api/v1.0/proxy',
          {
            method: 'GET',
            path: 'v3.0/messages/saved',
            parameters,
            subscriptionId,
          },
          { headers: { Authorization: `Bearer ${jwt}` } }
        );

        if (response.data?.error) {
          throw new Error(response.data.error.message || 'Erreur de chargement');
        }

        const nextMessages = Array.isArray(response.data) ? response.data : [];
        setSavedMessages((prev) => (append ? [...prev, ...nextMessages] : nextMessages));
        setOffset(nextOffset + nextMessages.length);
        setHasMore(nextMessages.length === PAGE_SIZE);
      } catch (requestError) {
        console.error('Error fetching saved messages:', requestError);
        setError(
          requestError?.message ||
            "Impossible de charger les messages enregistrés."
        );
        if (!append) {
          setSavedMessages([]);
          setOffset(0);
          setHasMore(false);
        }
      } finally {
        if (append) {
          setIsLoadingMore(false);
        } else {
          setIsLoading(false);
        }
      }
    },
    [selectedCollectionId, subscriptionId]
  );

  useEffect(() => {
    if (!isOpen) return;

    let isCancelled = false;

    const loadInitialData = async () => {
      try {
        setError('');
        const nextCollections = await fetchCollections();
        if (!isCancelled) {
          setCollections(nextCollections);
        }
      } catch (requestError) {
        console.error('Error fetching saved message collections:', requestError);
        if (!isCancelled) {
          setCollections([]);
        }
      }
    };

    loadInitialData();

    return () => {
      isCancelled = true;
    };
  }, [fetchCollections, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    fetchSavedMessages(0, false);
  }, [fetchSavedMessages, isOpen]);

  useEffect(() => {
    if (isOpen) return;
    setCollections([]);
    setSelectedCollectionId('');
    setSavedMessages([]);
    setOffset(0);
    setHasMore(false);
    setIsLoading(false);
    setIsLoadingMore(false);
    setError('');
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="saved-messages-modal-overlay"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="saved-messages-modal-content"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="saved-messages-modal-title"
      >
        <div className="saved-messages-modal-header">
          <div>
            <h3 id="saved-messages-modal-title">Messages enregistrés</h3>
            <p>Choisissez un message pour préremplir le brouillon actuel.</p>
          </div>
          <button
            type="button"
            className="saved-messages-modal-close"
            onClick={onClose}
            aria-label="Fermer les messages enregistrés"
          >
            ×
          </button>
        </div>

        <div className="saved-messages-modal-controls">
          <label htmlFor="saved-messages-collection-select">
            Collection
          </label>
          <select
            id="saved-messages-collection-select"
            className="saved-messages-modal-select"
            value={selectedCollectionId}
            onChange={(event) => setSelectedCollectionId(event.target.value)}
          >
            <option value="">Sans collection</option>
            {collections.map((collection) => (
              <option key={collection._id} value={collection._id}>
                {collection.name}
              </option>
            ))}
          </select>
        </div>

        <div className="saved-messages-modal-body">
          {isLoading ? (
            <div className="saved-messages-state">Chargement des messages enregistrés...</div>
          ) : error ? (
            <div className="saved-messages-state error">{error}</div>
          ) : savedMessages.length === 0 ? (
            <div className="saved-messages-state">
              Aucun message enregistré dans cette sélection.
            </div>
          ) : (
            <>
              <div className="saved-messages-list">
                {savedMessages.map((savedMessage) => {
                  const mediaList = Array.isArray(savedMessage.mediaList)
                    ? savedMessage.mediaList.filter(Boolean)
                    : [];
                  const displayText =
                    savedMessage.message?.trim() ||
                    savedMessage.name?.trim() ||
                    'Message sans texte';
                  const displayPrice = mediaList.length > 0
                    ? formatAmount(savedMessage.amount)
                    : null;

                  return (
                    <button
                      key={savedMessage._id}
                      type="button"
                      className="saved-message-card"
                      onClick={() => onApplySavedMessage(savedMessage)}
                    >
                      <div className="saved-message-card-top">
                        <div className="saved-message-card-title-group">
                          <span className="saved-message-card-name">
                            {savedMessage.name || 'Message enregistré'}
                          </span>
                          <span className="saved-message-card-meta">
                            {mediaList.length > 0
                              ? `${mediaList.length} média${mediaList.length > 1 ? 's' : ''}`
                              : 'Texte seul'}
                          </span>
                        </div>
                        {displayPrice && (
                          <span className="saved-message-card-price">{displayPrice}</span>
                        )}
                      </div>

                      <p className="saved-message-card-text">{displayText}</p>

                      {mediaList.length > 0 && (
                        <div className="saved-message-card-media">
                          {mediaList.slice(0, 4).map((media) => {
                            const previewUrl =
                              media?.src?.small || media?.src?.medium || media?.src?.normal;
                            return (
                              <div key={media._id} className="saved-message-card-thumb">
                                {previewUrl ? (
                                  <img src={previewUrl} alt="Média enregistré" />
                                ) : (
                                  <span>Media</span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}

                      <span className="saved-message-card-action">Utiliser ce message</span>
                    </button>
                  );
                })}
              </div>

              {hasMore && (
                <div className="saved-messages-modal-footer">
                  <button
                    type="button"
                    className="saved-messages-load-more"
                    onClick={() => fetchSavedMessages(offset, true)}
                    disabled={isLoadingMore}
                  >
                    {isLoadingMore ? 'Chargement...' : 'Charger plus'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SavedMessagesModal;
